import { css, styled, Theme } from "@mui/material";

import { getColor } from "@hooks";
import {
  cssForHeaderWithLinearGradient,
  cssPropertiesToString,
  WorkingArea,
} from "@utils";

const Dot = (corner: GridItemWithDotProps["corner"], theme: Theme) => css`
  content: "";
  position: absolute;
  width: 5px;
  height: 5px;
  border-radius: 100%;
  z-index: 1;

  background-color: var(--areaColor);

  outline: 6px solid ${getColor(x => x.tokens.bg["weak-50"], theme)};

  ${corner === "top-left" &&
  css`
    top: 0;
    left: 0;
    transform: translate(-50%, -50%);
  `}

  ${corner === "top-right" &&
  css`
    top: 0;
    right: 0;
    transform: translate(50%, -50%);
  `}

    ${corner === "bottom-left" &&
  css`
    bottom: 0;
    left: 0;
    transform: translate(-50%, 50%);
  `}

    ${corner === "bottom-right" &&
  css`
    bottom: 0;
    right: 0;
    transform: translate(50%, 50%);
  `}
`;

export const SectionHeader = styled("div")(
  ({ theme }) => css`
    > * {
      ${theme.breakpoints.up("mobile")} {
        margin: 0 16px;
      }

      ${theme.breakpoints.up("tablet")} {
        margin: 0 auto;
        max-width: 640px;
      }

      ${theme.breakpoints.up("desktop")} {
        max-width: 800px;
      }

      ${theme.breakpoints.up("desktopXl")} {
        max-width: 1000px;
      }
    }

    text-align: center;
    padding: 32px 0;
    margin: 16px 0;
    background: radial-gradient(
      50% 44.06% at 50% 100%,
      rgba(45, 238, 125, 0.12) 0%,
      rgba(118, 210, 204, 0) 100%
    );

    ${theme.breakpoints.up("mobile")} {
      ${cssPropertiesToString(theme.typography.paragraph_medium_medium)}
    }

    ${theme.breakpoints.up("tablet")} {
      ${cssPropertiesToString(theme.typography.paragraph_large_medium)}
    }

    ${theme.breakpoints.up("desktop")} {
      ${cssPropertiesToString(theme.typography.paragraph_xlarge_medium)}
    }

    h2 {
      ${cssForHeaderWithLinearGradient(theme)}

      ${theme.breakpoints.up("mobile")} {
        ${cssPropertiesToString(theme.typography.heading_h3_bold_mobile)}
      }

      ${theme.breakpoints.up("tablet")} {
        ${cssPropertiesToString(theme.typography.heading_h3_bold_tablet)}
      }

      ${theme.breakpoints.up("desktop")} {
        ${cssPropertiesToString(theme.typography.heading_h3_bold_desktop)}
      }
    }
  `,
);

export const SectionContainer = styled("div")(
  ({ theme }) => css`
    position: relative;

    ${theme.breakpoints.up("desktop")} {
      ::before {
        content: "";
        display: block;
        height: 100%;
        position: absolute;
        width: 3px;
        background: linear-gradient(
          180deg,
          rgba(24, 27, 37, 0) 10%,
          #96c5f0 35%,
          #05df54 50%,
          #96c5f0 65%,
          rgba(24, 27, 37, 0) 90%
        );

        ${theme.breakpoints.up("desktop")} {
          left: 20px;
        }

        ${theme.breakpoints.up("desktopXl")} {
          left: 55px;
        }
      }
    }

    ${theme.breakpoints.up("mobile")} {
      padding: 8px 16px;

      > div {
        margin-bottom: 32px;
      }
    }

    ${theme.breakpoints.up("tablet")} {
      margin: 16px auto;
      padding: 0 32px;
      width: ${theme.breakpoints.values.tablet}px;

      > div {
        margin-bottom: 48px;
      }
    }

    ${theme.breakpoints.up("desktop")} {
      margin: 16px auto;
      padding: 0 55px;
      width: ${theme.breakpoints.values.desktop}px;

      > div {
        margin-bottom: 80px;
      }
    }

    ${theme.breakpoints.up("desktopXl")} {
      margin: 16px auto;
      padding: 0 110px;
      width: ${theme.breakpoints.values.desktopXl}px;
    }
  `,
);

interface SpecializationColorProps {
  colorKey: WorkingArea;
}

export const SpeciazlizationColoredSpan = styled("span", {
  shouldForwardProp: prop => prop !== "colorKey",
})<SpecializationColorProps>(
  ({ theme, colorKey }) => css`
    color: ${getColor(c => c.tokens.casestudy[colorKey].dark, theme)};
    white-space: normal;

    ${theme.breakpoints.up("desktop")} {
      white-space: nowrap;
    }
  `,
);

export const GridContainer = styled("div", {
  shouldForwardProp: prop => prop !== "colorKey",
})<SpecializationColorProps>(
  ({ theme, colorKey }) => css`
    --areaColor: ${getColor(c => c.tokens.casestudy[colorKey].dark, theme)};
    --border-color: ${getColor(
      x =>
        theme.palette.mode === "light"
          ? x.tokens.bg["white-0"]
          : x.tokens.stroke["soft-200"],
      theme,
    )};

    ${theme.breakpoints.up("mobile")} {
      grid-template-columns: 1fr;
      grid-template-rows: auto;
      grid-auto-flow: row;
    }

    ${theme.breakpoints.up("tablet")} {
      grid-template-columns: min-content repeat(2, minmax(0, 1fr));
      grid-template-rows: auto auto auto;
      grid-template-areas:
        "header item1 item2"
        "header middle middle"
        "header item3 item4";
    }

    display: grid;
    text-align: center;

    border-right: 1px solid var(--border-color);
    border-bottom: 1px solid var(--border-color);

    ${theme.breakpoints.down("tablet")} {
      position: relative;
      ::before {
        ${Dot("bottom-left", theme)}
      }
      ::after {
        ${Dot("bottom-right", theme)}
      }
    }
  `,
);

export const GridItem = styled("div")(
  ({ theme }) => css`
    border-left: 1px solid var(--border-color);
    border-top: 1px solid var(--border-color);

    position: relative;
    text-align: left;

    ${theme.breakpoints.up("mobile")} {
      padding: 16px 8px;
    }

    ${theme.breakpoints.up("tablet")} {
      padding: 24px 16px;
    }

    ${theme.breakpoints.up("desktop")} {
      padding: 32px 24px;
    }

    h4 {
      ${cssPropertiesToString(theme.typography.paragraph_large_semibold)}
    }

    font-family: Work Sans;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;

    p {
      color: ${getColor(x => x.tokens.text["sub-600"], theme)};
    }

    em {
      font-style: normal;
      color: var(--areaColor);
    }
  `,
);

interface GridItemWithDotProps {
  corner: "top-left" | "top-right" | "bottom-left" | "bottom-right";
}

export const GridItemWithDot = styled(GridItem, {
  shouldForwardProp: prop => prop !== "corner",
})<GridItemWithDotProps>(
  ({ theme, corner }) => css`
    ${theme.breakpoints.up("tablet")} {
      ::after {
        ${Dot(corner, theme)}
      }
    }
  `,
);

export const GridItemHeader = styled(GridItem)(
  ({ theme }) => css`
    text-align: left;
    grid-area: header;

    ${theme.breakpoints.up("tablet")} {
      width: 240px;
    }

    ${theme.breakpoints.up("desktop")} {
      width: 280px;
    }

    ${theme.breakpoints.up("desktopXl")} {
      width: 360px;
    }

    div {
      margin-bottom: 16px;
    }

    h4 {
      ${cssPropertiesToString(theme.typography.paragraph_xxlarge_semibold)}
    }

    p {
      ${cssPropertiesToString(theme.typography.paragraph_medium_medium)}
      color: ${getColor(x => x.tokens.text["sub-600"], theme)};
      margin-bottom: 12px;
    }

    ${theme.breakpoints.down("tablet")} {
      position: relative;
      ::before {
        ${Dot("top-left", theme)}
      }
      ::after {
        ${Dot("top-right", theme)}
      }
    }
  `,
);

export const GridItemMiddle = styled(GridItem)(
  ({ theme }) => css`
    display: flex;
    color: ${getColor(x => x.tokens.text["sub-600"], theme)};
    justify-content: center;
    grid-area: middle;

    ${theme.breakpoints.up("tablet")} {
      padding: 11px 12px;
    }

    ${theme.breakpoints.up("desktop")} {
      padding: 11px 24px;
    }

    button {
      padding-bottom: 18px;
      color: var(--areaColor);
    }
  `,
);
